import { useState, useEffect, FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { buildFederatedQuery } from '../utils/federatedAuth';
import { Loader2, CheckCircle, ArrowLeft, Inbox } from 'lucide-react';
import { COMPANY_LOGO_PATH, COMPANY_NAME } from '../config/brand';

export function ActivatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { activateAccount, resendActivation, error } = useAuth();
  const [email, setEmail] = useState('');
  const [activationToken, setActivationToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const federatedParams = buildFederatedQuery({
    client_id: searchParams.get('client_id'),
    state: searchParams.get('state'),
    code_challenge: searchParams.get('code_challenge'),
    redirect_uri: searchParams.get('redirect_uri'),
  });
  const postActivatePath = federatedParams ? `/auth/federated-login${federatedParams}` : '/login';

  useEffect(() => {
    const emailParam = searchParams.get('email');
    const tokenParam = searchParams.get('token');
    if (emailParam) setEmail(emailParam);
    if (tokenParam) setActivationToken(tokenParam);
  }, [searchParams]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setIsLoading(true);
    try {
      await activateAccount(email, activationToken);
      setSuccess(true);
      setTimeout(() => navigate(postActivatePath), 2000);
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Activation failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) return setLocalError('Please enter your email');
    setIsResending(true);
    setResendSuccess(false);
    try {
      await resendActivation(email);
      setResendSuccess(true);
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Failed to resend');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Subtle enterprise atmosphere */}
      <div
        className="pointer-events-none fixed inset-0 opacity-40"
        style={{
          background:
            'radial-gradient(ellipse 80% 50% at 50% -20%, rgba(99, 102, 241, 0.18), transparent 55%), radial-gradient(ellipse 60% 40% at 100% 100%, rgba(99, 102, 241, 0.08), transparent 50%)',
        }}
      />

      <header className="relative z-10 flex items-center justify-between px-6 py-5 border-b border-border/60">
        <div className="flex items-center gap-2.5">
          <img
            src={COMPANY_LOGO_PATH}
            alt={`${COMPANY_NAME} Logo`}
            width={36}
            height={36}
            className="w-9 h-9 object-contain"
          />
          <span className="text-sm font-semibold tracking-tight text-foreground">Tyaus</span>
        </div>
        <Link
          to={postActivatePath}
          className="text-xs text-foreground-tertiary hover:text-foreground-secondary transition-colors"
        >
          Sign in
        </Link>
      </header>

      <main className="relative z-10 flex-1 flex items-start justify-center px-4 py-10 sm:py-16">
        <div className="w-full max-w-xl animate-fade-in">
          {success ? (
            <div className="bg-background-secondary/80 border border-border rounded-2xl p-12 text-center shadow-2xl backdrop-blur-sm">
              <CheckCircle className="w-14 h-14 text-success mx-auto mb-5" />
              <h1 className="text-2xl font-semibold tracking-tight mb-2">Account activated</h1>
              <p className="text-sm text-foreground-secondary">Redirecting you to sign in…</p>
            </div>
          ) : (
            <div className="bg-background-secondary/80 border border-border rounded-2xl shadow-2xl overflow-hidden backdrop-blur-sm">
              {/* Email chrome — Claude enterprise inbox style */}
              <div className="px-6 sm:px-8 pt-6 pb-4 border-b border-border/80">
                <div className="flex items-center gap-2 mb-4">
                  <Inbox className="w-3.5 h-3.5 text-foreground-muted" />
                  <span className="text-[11px] uppercase tracking-[0.14em] text-foreground-muted">
                    Inbox
                  </span>
                </div>
                <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-foreground leading-snug">
                  Activate Your Tyaus Account
                </h1>
              </div>

              <div className="px-6 sm:px-8 py-5 border-b border-border/80 flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-accent/20 border border-accent/30 flex items-center justify-center flex-shrink-0 text-xs font-semibold text-accent">
                  A
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm font-medium text-foreground truncate">admin</span>
                    <span className="text-[11px] text-foreground-muted whitespace-nowrap">
                      just now
                    </span>
                  </div>
                  <p className="text-xs text-foreground-tertiary mt-0.5">
                    to {email || 'me'}
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="px-6 sm:px-8 py-8 space-y-7">
                <div className="space-y-3">
                  <h2 className="text-lg font-semibold text-foreground tracking-tight">
                    Welcome to Tyaus
                  </h2>
                  <p className="text-sm text-foreground-secondary leading-relaxed max-w-md">
                    Thank you for signing up. Activate your account using the button or code below.
                  </p>
                </div>

                {(localError || error) && (
                  <div className="p-3 bg-error/10 border border-error/20 rounded-lg text-sm text-error">
                    {localError || error}
                  </div>
                )}
                {resendSuccess && (
                  <div className="p-3 bg-success/10 border border-success/20 rounded-lg text-sm text-success">
                    A new activation email has been sent.
                  </div>
                )}

                <div className="space-y-2">
                  <label htmlFor="email" className="label">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input"
                    required
                    autoComplete="email"
                  />
                </div>

                <div className="space-y-3">
                  <label htmlFor="code" className="text-sm text-foreground-secondary">
                    Activation code
                  </label>
                  <input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    value={activationToken}
                    onChange={(e) =>
                      setActivationToken(e.target.value.replace(/\D/g, '').slice(0, 6))
                    }
                    className="w-full px-4 py-4 bg-background border border-border rounded-xl text-center text-3xl tracking-[0.35em] font-mono text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent transition-colors"
                    placeholder="000000"
                    maxLength={6}
                    required
                    aria-label="Activation code"
                  />
                  <p className="text-xs text-foreground-muted">
                    This link and code expire in 24 hours.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isLoading || activationToken.length !== 6}
                  className="btn-primary w-full py-3 text-sm font-semibold rounded-xl"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Activating…
                    </>
                  ) : (
                    'Activate Account'
                  )}
                </button>

                <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={isResending}
                    className="text-accent hover:text-accent-hover transition-colors disabled:opacity-50"
                  >
                    {isResending ? 'Sending…' : 'Resend activation email'}
                  </button>
                  <Link
                    to={postActivatePath}
                    className="flex items-center gap-1.5 text-foreground-tertiary hover:text-foreground-secondary transition-colors"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    Back to login
                  </Link>
                </div>
              </form>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
